// @flow
import { useMemo } from 'react';
import { useQuery } from 'react-query';
import NaurService from '../../services/naurService';
import useAbortController from '../useAbortController';

/**
 * Read-only preflight for the one-time applicable fraction initialization.
 *
 * `enabled` is driven by the confirmation dialog being open — this never fires on page
 * load. `staleTime: 0` means a reopened dialog paints the cached body and refetches
 * behind it, so callers must gate their confirm action on `isFetching`, not `isLoading`.
 */
const useApplicableFractionInitializationPreflight = ({
  organizationId,
  propertyId,
  enabled,
}: {
  organizationId: ?string,
  propertyId: ?string,
  enabled: boolean,
}) => {
  const naurService = useMemo(() => new NaurService(), []);
  const abortControllerOptions = useAbortController();

  return useQuery(
    ['applicable-fraction-initialization', propertyId],
    () =>
      naurService.getApplicableFractionInitialization(
        organizationId,
        propertyId,
        abortControllerOptions,
      ),
    {
      enabled: Boolean(enabled && organizationId && propertyId),
      staleTime: 0,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );
};

export default useApplicableFractionInitializationPreflight;
